/**
 * LOT-SW-REPOS — MISE À JOUR DU SERVICE WORKER APPLIQUÉE AU REPOS.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LA CAUSE RACINE QUE CE MODULE RETIRE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Jusqu'ici la PWA était en `registerType: 'autoUpdate'` + `skipWaiting` +
 * `clientsClaim` : à chaque déploiement, le NOUVEAU service worker s'installait,
 * sautait l'attente et prenait le contrôle des onglets DÉJÀ OUVERTS. Son
 * activation purgeait l'ancien precache. Un onglet en cours qui chargeait
 * ensuite une page paresseuse (`React.lazy`) demandait un ancien chunk haché :
 * absent du precache → réseau → le statique Railway répond 200 `text/html` →
 * `import()` rejette → crash de rendu. La PR #126 attrape la CHUTE (B1 : le
 * boundary interne préserve l'appel ; B2 : rechargement au repos sur
 * `vite:preloadError`). Ce module retire la TRAPPE : le nouveau service worker
 * ATTEND (`skipWaiting: false`), l'ancien continue de servir l'ancien precache
 * aux onglets ouverts, et c'est l'application qui décide QUAND basculer.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LA RÈGLE : SILENCIEUX AU REPOS, DIFFÉRÉ EN APPEL
 * ══════════════════════════════════════════════════════════════════════════
 *
 *  - Une mise à jour EN ATTENTE (`onNeedRefresh`) est appliquée AUTOMATIQUEMENT
 *    et SANS RIEN DEMANDER dès que le poste est au repos : prédicat
 *    `callMachine.getSnapshot().phase === 'idle'` — le même que B2. `wrapup`
 *    (qualification en cours de saisie), `preview` (enchaînement de campagne
 *    armé) et toute phase de ligne vivante DIFFÈRENT l'application.
 *  - Différée, elle attend un CHANGEMENT DE PHASE de la machine (abonnement au
 *    store) ou un retour au premier plan (`visibilitychange`) — jamais une
 *    minuterie serrée. Un bandeau discret (« s'installera à la fin de
 *    l'appel ») est alors publié via le store exporté ici.
 *  - Appliquer = `updateSW(true)` : message `SKIP_WAITING` au worker en
 *    attente ; `workbox-window` recharge la page au `controllerchange`
 *    (événement `controlling`, `isUpdate` vrai dès qu'un worker contrôlait la
 *    page à l'enregistrement). Ceinture : on écoute aussi `controllerchange`
 *    nous-mêmes — le seul cas où `isUpdate` est faux est un onglet ouvert
 *    AVANT toute installation (première visite), et il mérite le même
 *    rechargement.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * GARDE ANTI-BOUCLE : AU PLUS UN RECHARGEMENT PAR VERSION
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `__BUILD_ID__` (défini par `vite.config.ts`) identifie la version de la page
 * COURANTE. Avant d'appliquer, on mémorise cet identifiant dans
 * `sessionStorage`. Après rechargement, si la page porte un identifiant
 * DIFFÉRENT, la mise à jour a pris : le marqueur est caduc et une prochaine
 * mise à jour pourra s'appliquer. Si la page porte ENCORE le même identifiant
 * (le rechargement n'a pas ramené la nouvelle version — cache intermédiaire,
 * déploiement qui oscille entre deux versions…) et qu'une mise à jour est de
 * nouveau en attente, on ne recharge PAS une seconde fois : on laisse la main
 * (B2 reste en ceinture, et la prochaine navigation complète fera le travail).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * VÉRIFICATION PÉRIODIQUE MODESTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Le navigateur ne revérifie `sw.js` que sur navigation. Un onglet PWA iPad
 * reste ouvert des jours : sans `registration.update()` il ne verrait JAMAIS
 * la nouvelle version. On vérifie toutes les 30 minutes et à chaque retour au
 * premier plan. Une vérification n'installe le nouveau worker qu'EN ATTENTE :
 * elle ne touche ni à l'ancien worker ni à son precache — inoffensive pendant
 * un appel.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER N'IMPORTE PAS `virtual:pwa-register`
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Le module virtuel n'existe que sous le plugin Vite : vitest ne le résout
 * pas. Toute la logique vit donc ICI, avec ses dépendances INJECTÉES
 * (`registerSW`, machine, document, storage, horloge). Le branchement réel
 * (`registerServiceWorker.ts`) importe le module virtuel et appelle
 * `installServiceWorkerUpdates` avec les vraies dépendances ; c'est le VRAI
 * code de décision qui est exécuté par la spec.
 *
 * LIMITE CONNUE — MULTI-ONGLETS : le service worker est partagé par tous les
 * onglets de l'origine. Un onglet au repos qui applique la mise à jour fait
 * basculer AUSSI un autre onglet de la même origine en appel (même poste).
 * L'usage cible (PWA iPad, un onglet) n'y expose pas ; B1/B2 restent la
 * ceinture pour ce cas. Documenté dans `docs/architecture/PWA-MISE-A-JOUR.md`.
 */

export type SwUpdateStatus =
  /** Aucune mise à jour en attente (ou déjà appliquée : la page va se recharger). */
  | 'none'
  /** Mise à jour en attente, poste occupé : appliquée au retour au repos. Bandeau. */
  | 'deferred'
  /** `updateSW(true)` envoyé : le rechargement suit le `controllerchange`. */
  | 'applying'
  /** Déjà rechargé une fois pour cette version : on ne boucle pas. Silencieux. */
  | 'blocked'

export interface SwUpdateSnapshot {
  status: SwUpdateStatus
}

/** Clé `sessionStorage` : identifiant de build de la page qui a appliqué une MAJ. */
export const SW_UPDATE_APPLIED_FOR_BUILD_KEY = 'sw-update-applied-for-build'
/** Vérification modeste des mises à jour : 30 minutes. */
export const SW_UPDATE_CHECK_INTERVAL_MS = 30 * 60_000

/** Sous-ensemble de `RegisterSWOptions` (vite-plugin-pwa) réellement utilisé. */
export interface SwRegisterOptions {
  immediate?: boolean
  onNeedRefresh?: () => void
  onRegisteredSW?: (swScriptUrl: string, registration: ServiceWorkerRegistration | undefined) => void
  onRegisterError?: (error: unknown) => void
}

export type SwRegisterFn = (options: SwRegisterOptions) => (reloadPage?: boolean) => Promise<void>

/** Ce qu'on attend d'un `ServiceWorkerRegistration` : pouvoir revérifier. */
export interface SwRegistrationLike {
  update(): Promise<unknown>
}

export interface SwUpdateDeps {
  /** `registerSW` de `virtual:pwa-register` (ou une doublure). */
  registerSW: SwRegisterFn
  /** La machine d'appel v2 (`callMachine`) : store externe, `subscribe`/`getSnapshot`. */
  machine: {
    subscribe(listener: () => void): () => void
    getSnapshot(): { phase: string }
  }
  /** Identifiant de la version de la page courante (`__BUILD_ID__`). */
  currentBuildId: string
  doc: Pick<Document, 'addEventListener' | 'visibilityState'>
  win: {
    setInterval(handler: () => void, ms: number): unknown
    location: { reload(): void }
  }
  storage: Pick<Storage, 'getItem' | 'setItem'>
  /** `navigator.serviceWorker` — ceinture `controllerchange` → reload. Absent hors navigateur. */
  serviceWorkerContainer?: Pick<ServiceWorkerContainer, 'addEventListener'>
  /** Prédicat de repos. Défaut : `machine.getSnapshot().phase === 'idle'`. */
  isIdle?: () => boolean
  checkIntervalMs?: number
}

// ─── Store externe (lu par le bandeau via useSyncExternalStore) ─────────────

type Listener = () => void
const listeners = new Set<Listener>()
let snapshot: SwUpdateSnapshot = { status: 'none' }

function setStatus(status: SwUpdateStatus) {
  if (snapshot.status === status) return
  snapshot = { status }
  listeners.forEach((listener) => listener())
}

export const swUpdateStore = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  getSnapshot(): SwUpdateSnapshot {
    return snapshot
  },
}

/** Réservé aux tests. */
export function resetSwUpdateStore() {
  setStatus('none')
}

// ─── Installation ───────────────────────────────────────────────────────────

export interface SwUpdateHandle {
  /** Force une tentative d'application (exposé pour la recette et les tests). */
  tryApply(): void
  /** Lance une vérification `registration.update()` si l'enregistrement est connu. */
  check(): void
  dispose(): void
}

export function installServiceWorkerUpdates(deps: SwUpdateDeps): SwUpdateHandle {
  const isIdle = deps.isIdle ?? (() => deps.machine.getSnapshot().phase === 'idle')
  const checkIntervalMs = deps.checkIntervalMs ?? SW_UPDATE_CHECK_INTERVAL_MS

  let pending = false
  let applied = false
  let disposed = false
  let registration: SwRegistrationLike | undefined
  let unsubscribeMachine: (() => void) | undefined

  setStatus('none')

  const alreadyReloadedForThisBuild = (): boolean => {
    try {
      return deps.storage.getItem(SW_UPDATE_APPLIED_FOR_BUILD_KEY) === deps.currentBuildId
    } catch {
      return false
    }
  }

  const stopWatchingMachine = () => {
    unsubscribeMachine?.()
    unsubscribeMachine = undefined
  }

  const apply = async () => {
    applied = true
    stopWatchingMachine()
    setStatus('applying')
    try {
      deps.storage.setItem(SW_UPDATE_APPLIED_FOR_BUILD_KEY, deps.currentBuildId)
    } catch {
      // sessionStorage inaccessible : on applique quand même — la boucle
      // éventuelle est bornée par B2 (une minute) et par le serveur.
    }
    // Ceinture : `workbox-window` recharge sur `controlling` quand `isUpdate`
    // est vrai ; le cas restant (page jamais contrôlée à l'enregistrement)
    // mérite le même rechargement.
    deps.serviceWorkerContainer?.addEventListener(
      'controllerchange',
      () => deps.win.location.reload(),
      { once: true },
    )
    try {
      await updateSW(true)
    } catch (error) {
      console.warn('[pwa] Application de la mise à jour du service worker échouée', error)
    }
  }

  const tryApply = () => {
    if (disposed || !pending || applied) return
    if (alreadyReloadedForThisBuild()) {
      // Déjà rechargé une fois pour cette version et on la porte encore : on
      // ne boucle pas. Silencieux — B2 reste en ceinture.
      setStatus('blocked')
      stopWatchingMachine()
      console.warn(
        '[pwa] Mise à jour en attente ignorée : déjà rechargé une fois pour cette version',
        { buildId: deps.currentBuildId },
      )
      return
    }
    if (!isIdle()) {
      // Poste occupé : on attend le prochain changement de phase. Bandeau.
      setStatus('deferred')
      if (!unsubscribeMachine) unsubscribeMachine = deps.machine.subscribe(tryApply)
      return
    }
    void apply()
  }

  const check = () => {
    if (disposed || !registration) return
    registration.update().catch(() => {
      // Hors ligne ou serveur injoignable : la prochaine vérification réessaiera.
    })
  }

  const updateSW = deps.registerSW({
    immediate: true,
    onNeedRefresh() {
      pending = true
      tryApply()
    },
    onRegisteredSW(_swScriptUrl, reg) {
      registration = reg
      if (reg) deps.win.setInterval(check, checkIntervalMs)
    },
    onRegisterError(error) {
      console.warn('[pwa] Enregistrement du service worker échoué', error)
    },
  })

  const onVisibilityChange = () => {
    if (deps.doc.visibilityState !== 'visible') return
    check()
    tryApply()
  }
  deps.doc.addEventListener('visibilitychange', onVisibilityChange)

  return {
    tryApply,
    check,
    dispose() {
      disposed = true
      stopWatchingMachine()
    },
  }
}
